import { useMemo } from 'react';
import { useAuth } from './useAuth';
import { useGetSystemSettingsQuery } from '../services/nestjs-base-api/admin-api';

/**
 * Hook to get a system setting value by name
 * Falls back to feature value if system settings are not available
 * 
 * @param settingName - The name of the system setting (e.g., 'MaxEvents', 'MaxGalleriesPerEvent', 'MaxPhotosPerGallery')
 * @param defaultValue - Default value if setting is not found
 * @returns The setting value as a string
 */
export function useSystemSetting(settingName: string, defaultValue: string): string {
  const { user } = useAuth();
  
  // Fetch system settings
  const { data: systemSettings, isLoading, error } = useGetSystemSettingsQuery(
    { jwtToken: user?.jwtToken || '' },
    { skip: !user?.jwtToken }
  );

  // Find the specific setting value
  return useMemo(() => {
    if (!systemSettings || isLoading || error) {
      return defaultValue;
    }

    const setting = systemSettings.find(s => s.name === settingName);
    return setting?.value || defaultValue;
  }, [systemSettings, settingName, defaultValue, isLoading, error]);
}

/**
 * Convenience hooks for specific system settings
 */
export function useMaxEvents(defaultValue: string = '10'): string {
  return useSystemSetting('MaxEvents', defaultValue);
}

export function useMaxGalleriesPerEvent(defaultValue: string = '10'): string {
  return useSystemSetting('MaxGalleriesPerEvent', defaultValue);
}

export function useMaxPhotosPerGallery(defaultValue: string = '2000'): string {
  return useSystemSetting('MaxPhotosPerGallery', defaultValue);
}

export function useGalleryRefreshInterval(defaultValue: string = '3000'): string {
  return useSystemSetting('GalleryRefreshInterval', defaultValue);
}
